import React, { useState } from 'react';
import { AlertTriangle, Shield, CheckCircle } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { TITLE } from '../config/domain';

interface AdultDisclaimerProps {
  onAccept: () => void;
  onReject: () => void;
}

const AdultDisclaimer: React.FC<AdultDisclaimerProps> = ({ onAccept, onReject }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(true);
  const [isClosing, setIsClosing] = useState(false);

  const handleAccept = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      onAccept();
    }, 300);
  };

  const handleReject = () => {
    setIsClosing(true);
    setTimeout(() => {
      onReject();
      // Redirect to Google
      window.location.href = 'https://www.google.com';
    }, 300);
  };

  if (!isVisible) return null;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
      isClosing ? 'opacity-0' : 'opacity-100'
    }`}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm"></div>
      
      {/* Modal */}
      <div className={`relative bg-white rounded-2xl shadow-2xl max-w-md transform transition-all duration-300 ${
        isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
      }`}
      style={{ position: 'absolute', top: '20px' }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-pink-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-white/20 rounded-full p-3">
              <AlertTriangle className="h-8 w-8 text-white" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-center mb-2">
            {t('adultDisclaimer.title')}
          </h2>
          <p className="text-center text-red-100 hidden">
            {t('adultDisclaimer.subtitle')}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="flex items-start space-x-3">
            <div className="bg-yellow-100 rounded-full p-2 mt-1">
              <Shield className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-1">
                {t('adultDisclaimer.ageVerificationTitle')}
              </h3>
              <p className="text-sm text-gray-600"
              style={{ height: '40px' }}
              >
                {t('adultDisclaimer.ageVerificationText')}
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="bg-blue-100 rounded-full p-2 mt-1">
              <CheckCircle className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-1">
                {t('adultDisclaimer.informedConsentTitle')}
              </h3>
              <p className="text-sm text-gray-600"
              style={{ height: '40px' }}
              >
                {t('adultDisclaimer.informedConsentText')}
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mt-6">
            <p className="text-sm text-gray-700 text-center"
            style={{ height: '80px' }}
            >
              <strong>{TITLE}</strong> {t('adultDisclaimer.platformDescription')}
            </p>
          </div>

          <div className="border-t pt-4 mt-6 hidden">
            <p className="text-xs text-gray-500 text-center mb-4">
              Confermando l'accesso dichiari di:
            </p>
            <ul className="text-xs text-gray-600 space-y-1 mb-6">
              <li className="flex items-center">
                <span className="w-2 h-2 bg-pink-500 rounded-full mr-2"></span>
                Avere almeno 18 anni di età
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-pink-500 rounded-full mr-2"></span>
                Accettare la visualizzazione di contenuti per adulti
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-pink-500 rounded-full mr-2"></span>
                Essere legalmente autorizzato a visualizzare tali contenuti
              </li>
            </ul>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 pt-0 space-y-3">
          <button
            onClick={handleAccept}
            className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
            aria-label={t('adultDisclaimer.acceptButton')}
            style={{ height: '80px', fontSize: '13px' }}
          >
            {t('adultDisclaimer.acceptButton')}
          </button>
          
          <button
            onClick={handleReject}
            className="w-full bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
            aria-label={t('adultDisclaimer.rejectButton')}
            style={{ fontSize: '10px' }}
          >
            {t('adultDisclaimer.rejectButton')}
          </button>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 rounded-b-2xl p-4">
          <p className="text-xs text-gray-500 text-center">
            {t('adultDisclaimer.footerText')}{' '}
            <a href="/terms" className="text-pink-600 hover:underline font-bold">
              {t('adultDisclaimer.termsLink')}
            </a>{' '}
            {t('adultDisclaimer.footerAnd')}{' '}
            <a href="/privacy" className="text-pink-600 hover:underline font-bold">
              {t('adultDisclaimer.privacyLink')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdultDisclaimer;
